"use client";

import React, { useMemo } from "react";
import { useForm, type UseFormReturn } from "react-hook-form";
import { zodResolver } from "@/lib/utils/validation/zod-react-hook-form-resolver";
import { useUniversalAuth } from "@wtc/ui";

// Utilities
import { buildFormSchema } from "./form-schema";
import { buildDefaultValues } from "./form-defaults";
import { createFormDataCleaner } from "./form-data-cleaner";
import { useClientSelectData } from "./hooks/useClientSelectData";
import { useClientSelfContact } from "@/lib/hooks/auth/use-client-self-contact";
import { useFormAutoSave } from "./hooks/useFormAutoSave";

// Custom hooks
import { useFormState } from "./hooks/useFormState";
import { useFormValidation } from "./hooks/useFormValidation";
import { useFormSubmission } from "./hooks/useFormSubmission";
import { useTemplateHandling } from "./hooks/useTemplateHandling";
import { useFormFieldContext } from "./hooks/useFormFieldContext";

// UI Components
import { FormStatusAlerts } from "./FormStatusAlerts";
import { FormHistoryTab } from "./FormHistoryTab";
import { FormHeader } from "./components/FormHeader";
import { FormTabs } from "./components/FormTabs";
import { FormContent } from "./components/FormContent";
import { FormModals } from "./components/FormModals";
import { AIAssistantPanel } from "./components/AIAssistantPanel";

// Types
import type { BaseEntity, SimpleTableConfig, EnhancedGenericFormProps } from "./types";
import type { SimpleFieldConfig } from "../UniversalCRMTable/types/index";

/**
 * Main form component - orchestrates form state, validation, and rendering.
 * Delegates specific concerns to custom hooks and sub-components.
 */
const EnhancedGenericCreateFormComponent = React.memo(function EnhancedGenericCreateForm<
  T extends BaseEntity = BaseEntity,
>({
  entityName,
  config,
  onSubmit,
  onCancel,
  initialValues = {},
  isLoading = false,
  validationSchema,
  customComponents = {},
  templateSelector: TemplateSelector,
  saveTemplateModal: SaveTemplateModal,
  tagSets = {},
  statusOptions = [],
  priorityOptions = [],
  fullPage = false,
  expandInlineVariant = "replace",
}: EnhancedGenericFormProps<T>) {
  const { appUser } = useUniversalAuth();

  // Form state management
  const formState = useFormState();

  // Check if we need client search (has client-select field)
  const hasClientSelectField = useMemo(() => {
    return Object.values(config.fields).some((field: any) => field.type === "client-select");
  }, [config.fields]);

  // Client search hook
  const { clientOptions, clientsLoading, searchValue, setSearchValue, isSearching } =
    useClientSelectData(hasClientSelectField);

  // Client self-contact resolution — lets a client_user "assign to me" on a
  // client-select field instead of searching for themselves. Gated to clients
  // + forms that actually have a client-select field, so employee forms don't
  // fetch the accessible-clients payload.
  const {
    isClientUser,
    selfContactId: clientSelfContactId,
    selfContactName: clientSelfContactName,
  } = useClientSelfContact(hasClientSelectField);

  // Create validation schema
  const formSchema = useMemo(() => {
    return buildFormSchema(config, validationSchema);
  }, [config, validationSchema]);

  // Create default values
  const defaultValues = useMemo(() => {
    return buildDefaultValues(config, initialValues, appUser?.id);
  }, [config, initialValues, appUser?.id]);

  // Form setup
  const form = useForm({
    resolver: zodResolver(formSchema as any),
    defaultValues,
  });

  // Auto-save form data to history
  useFormAutoSave(form as any, {
    entityName,
    debounceMs: 2000,
    enabled: true,
  });

  // Create form data cleaner
  const cleanFormData = useMemo(() => {
    return createFormDataCleaner(config, appUser?.id);
  }, [config, appUser?.id]);

  // Validation hook
  const validation = useFormValidation({
    form,
    config,
    submissionError: formState.submissionError,
    setSubmissionError: formState.setSubmissionError,
    setHasValidationErrors: formState.setHasValidationErrors,
    setShowErrorPopup: formState.setShowErrorPopup,
    setManualErrorInfo: formState.setManualErrorInfo,
    setErrorFieldNames: formState.setErrorFieldNames,
  });

  // Submission hook
  const submission = useFormSubmission({
    form,
    config,
    entityName,
    isCreating: formState.isCreating,
    setIsCreating: formState.setIsCreating,
    setSubmissionError: formState.setSubmissionError,
    setHasValidationErrors: formState.setHasValidationErrors,
    setIsSuccessfullySubmitted: formState.setIsSuccessfullySubmitted,
    setSelectedTemplate: formState.setSelectedTemplate,
    onSubmit,
    cleanFormData,
    extractErrorFields: validation.extractErrorFields,
    extractZodErrorFields: validation.extractZodErrorFields,
  });

  // Template handling hook
  const templateHandling = useTemplateHandling({
    form,
    selectedTemplate: formState.selectedTemplate,
    setSelectedTemplate: formState.setSelectedTemplate,
    isSaveTemplateModalOpen: formState.isSaveTemplateModalOpen,
    setIsSaveTemplateModalOpen: formState.setIsSaveTemplateModalOpen,
  });

  // Field context hook
  const fieldContext = useFormFieldContext({
    form,
    config,
    customComponents,
    appUserId: appUser?.id,
    clientOptions,
    clientsLoading,
    isSearching,
    searchValue,
    setSearchValue,
    isClientUser,
    clientSelfContactId,
    clientSelfContactName,
    expandInlineVariant,
  });

  // Get editable fields (optionally ordered by config.createFormFieldOrder)
  const editableFields = useMemo(() => {
    const entries = Object.entries(config.fields).filter(
      ([_, field]: [string, any]) => field.editable !== false
    );
    const order = config.createFormFieldOrder;
    if (!order?.length) return entries;
    const byKey = new Map(entries);
    const ordered: [string, SimpleFieldConfig][] = [];
    for (const key of order) {
      if (byKey.has(key)) {
        ordered.push([key, byKey.get(key)!]);
        byKey.delete(key);
      }
    }
    byKey.forEach((field, key) => ordered.push([key, field]));
    return ordered;
  }, [config.fields, config.createFormFieldOrder]);

  // Memoize form data for template modal
  const memoizedFormData = useMemo(() => form.watch(), [form]);

  // Compute progressive-disclosure sections from config (once). Falls back to
  // flat-grid layout in FormContent when undefined. See plan
  // `the-log-activity-modal-enumerated-whisper.md`.
  const formSections = useMemo(() => {
    return config.createFormSections ? config.createFormSections() : undefined;
  }, [config]);

  // Form content for tabs (single form per entity; id helps avoid duplicate submit from hidden forms)
  const formContent = (
    <FormContent
      entityName={entityName}
      editableFields={editableFields}
      createFieldContext={fieldContext.createFieldContext}
      TemplateSelector={TemplateSelector}
      handleTemplateSelect={templateHandling.handleTemplateSelect}
      selectedTemplate={templateHandling.selectedTemplate}
      isCreating={formState.isCreating}
      isLoading={isLoading}
      isSubmitting={form.formState.isSubmitting}
      hasValidationErrors={formState.hasValidationErrors}
      submissionError={formState.submissionError}
      displayName={config.displayName}
      onSubmit={submission.handleSubmitClick}
      onSaveTemplate={SaveTemplateModal ? templateHandling.handleOpenSaveTemplateModal : undefined}
      onCancel={onCancel}
      onFormSubmit={form.handleSubmit(
        submission.handleFormSubmit,
        validation.handleValidationError
      )}
      sections={formSections}
      form={form as UseFormReturn<Record<string, unknown>>}
    />
  );

  // History content for tabs
  const historyContent = (
    <FormHistoryTab form={form as any} entityName={entityName} config={config as any} />
  );

  return (
    <div className="space-y-4 sm:space-y-6 max-w-full relative">
      <FormHeader displayName={config.displayName} />

      <AIAssistantPanel
        entityName={entityName}
        config={config}
        form={form}
      />

      <FormTabs
        activeTab={formState.activeTab}
        onTabChange={formState.setActiveTab}
        formContent={formContent}
        historyContent={historyContent}
        fullPage={fullPage}
      />

      {/* Status Display Section - Right below tabs for visibility */}
      <FormStatusAlerts
        hasValidationErrors={formState.hasValidationErrors}
        errorInfo={validation.errorInfo}
        submissionError={formState.submissionError}
        onDismissError={() => formState.setSubmissionError(null)}
      />

      <FormModals
        SaveTemplateModal={SaveTemplateModal}
        isSaveTemplateModalOpen={templateHandling.isSaveTemplateModalOpen}
        onCloseSaveTemplateModal={templateHandling.handleCloseSaveTemplateModal}
        formData={memoizedFormData as Record<string, unknown>}
      />
    </div>
  );
});

// Export the memoized component with proper generic typing
export const EnhancedGenericCreateForm = EnhancedGenericCreateFormComponent as <
  T extends BaseEntity = BaseEntity,
>(
  props: EnhancedGenericFormProps<T>
) => React.ReactElement;

// Factory function
export function createEnhancedGenericForm<T extends BaseEntity = BaseEntity>(
  entityName: string,
  config: SimpleTableConfig<T>
) {
  return React.memo(function EnhancedGenericForm(
    props: Omit<EnhancedGenericFormProps<T>, "entityName" | "config">
  ) {
    return (
      <EnhancedGenericCreateForm
        entityName={entityName}
        config={config}
        {...props}
      />
    );
  });
}
